import { Box, Flex, Text, Heading } from '@chakra-ui/react';
import {
  MdHome as RecipesIcon,
  MdFolder as CollectionsIcon,
  MdAdd as AddIcon,
} from 'react-icons/md';
import { useLocation } from 'wouter';
import logoFull from '@/assets/logo-full.png';
import { useAllCollections } from '@/lib/collectionService';
import { ROUTES } from '@/types';
import DesktopSidebarNavItem from './DesktopSidebarNavItem';
import DesktopSidebarProfile from './DesktopSidebarProfile';
import { useImportSheet } from '@/contexts/ImportSheetContext';

export default function DesktopSidebar() {
  const [location, navigate] = useLocation();
  const { open: openImportSheet } = useImportSheet();

  const { collections } = useAllCollections();

  const isBrowseActive = location === '/' || location.startsWith('/recipes');
  const isCollectionsActive = location === '/recipe-groups' || location.startsWith('/recipe-groups/');

  return (
    <Box
      as="nav"
      aria-label="Main navigation"
      display={{ base: 'none', lg: 'flex' }}
      flexDirection="column"
      w="260px"
      minW="260px"
      h="100vh"
      bg="bg.surface"
      borderRight="1px solid"
      borderColor="border.muted"
      overflowY="auto"
      overflowX="hidden"
      flexShrink={0}
      position="sticky"
      top={0}
    >
      {/* Logo */}
      <Flex
        align="center"
        gap={2.5}
        px={4}
        pt={5}
        pb={4}
        cursor="pointer"
        onClick={() => navigate(ROUTES.HOME())}
        flexShrink={0}
      >
        <img src={logoFull} alt="" style={{ height: '30px', width: 'auto', flexShrink: 0 }} />
        <Heading
          fontFamily="heading"
          fontWeight="800"
          fontSize="18px"
          color="text.primary"
          whiteSpace="nowrap"
        >
          Byte <Text as="span" color="accent.emphasis">Pantry</Text>
        </Heading>
      </Flex>

      {/* Main nav */}
      <Box px={2} flex={1}>

        {/* ── Browse ── */}
        <DesktopSidebarNavItem
          icon={RecipesIcon}
          label="Home"
          isActive={isBrowseActive}
          onClick={() => navigate(ROUTES.HOME())}
        />

        {/* Add a Recipe — opens import sheet */}
        <Flex
          as="button"
          align="center"
          gap="7px"
          w="100%"
          pl="30px"
          pr={3}
          py="6px"
          borderRadius="7px"
          color="text.muted"
          fontFamily="body"
          fontSize="13px"
          cursor="pointer"
          textAlign="left"
          transition="background 0.12s, color 0.12s"
          _hover={{ bg: 'bg.muted', color: 'accent.secondary' }}
          onClick={() => openImportSheet('url')}
        >
          <AddIcon size={14} />
          <Text>Add a Recipe</Text>
        </Flex>

        <Box h={3} />

        {/* ── Collections ── */}
        <DesktopSidebarNavItem
          icon={CollectionsIcon}
          label="Collections"
          isActive={isCollectionsActive && !location.startsWith('/recipe-groups/')}
          onClick={() => navigate(ROUTES.RECIPE_GROUPS_BASE())}
        />

        {collections && collections.length > 0 && (
          <Box>
            {collections.map((col) => (
              <DesktopSidebarNavItem
                key={col.id}
                icon={CollectionsIcon}
                label={col.title}
                isActive={location === ROUTES.RECIPE_GROUP(col.id)}
                onClick={() => navigate(ROUTES.RECIPE_GROUP(col.id))}
                isSubItem
              />
            ))}
          </Box>
        )}

        {/* Add a Collection */}
        <Flex
          as="button"
          align="center"
          gap="7px"
          w="100%"
          pl="30px"
          pr={3}
          py="6px"
          borderRadius="7px"
          color="text.muted"
          fontFamily="body"
          fontSize="13px"
          cursor="pointer"
          textAlign="left"
          transition="background 0.12s, color 0.12s"
          _hover={{ bg: 'bg.muted', color: 'accent.secondary' }}
          onClick={() => openImportSheet('collection')}
        >
          <AddIcon size={14} />
          <Text>Add a Collection</Text>
        </Flex>

      </Box>

      {/* Profile */}
      <DesktopSidebarProfile />
    </Box>
  );
}
